import { Button } from '@/components/ui/button';
import {
Tooltip,
TooltipContent,
TooltipTrigger,
} from '@/components/ui/tooltip';
import { useThemeStore } from '@/store/themeStore';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';
import { UserRole } from '@shared/types';
import {
BarChart3,
Barcode,
Boxes,
CalendarClock,
CheckSquare,
ChevronDown,
ChevronLeft,
ChevronRight,
ClipboardList,
Clock,
DatabaseZap,
Factory,
FileText,
FileUp,
GanttChart,
Gauge,
GitCompare,
HeartHandshake,
Image,
Activity,
AlertTriangle,
Layers3,
Link,
LayoutDashboard,
Package,
PackageOpen,
PanelsTopLeft,
RotateCcw,
ScanLine,
Settings,
ShieldCheck,
Shuffle,
Truck,
UserCog,
Warehouse,
Webhook,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink,useLocation } from 'react-router';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  allowedRoles?: UserRole[];
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/', icon: LayoutDashboard },
    ],
  },
  {
    id: 'production',
    label: 'Production',
    items: [
      { id: 'produce', label: 'Build / Produce', path: '/produce', icon: Factory },
      { id: 'manufacturing', label: 'Work Orders', path: '/manufacturing', icon: ClipboardList },
      { id: 'scheduling', label: 'Scheduling', path: '/scheduling', icon: GanttChart },
      { id: 'labor', label: 'Labor Tracking', path: '/labor', icon: Clock },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    items: [
      { id: 'inventory', label: 'Inventory', path: '/inventory', icon: Package },
      { id: 'inventory-import', label: 'Import', path: '/inventory/import', icon: FileUp, allowedRoles: [UserRole.ADMIN, UserRole.MANAGER] },
      { id: 'warehouses', label: 'Warehouses', path: '/inventory/warehouses', icon: Warehouse },
      { id: 'transfers', label: 'Transfers', path: '/inventory/transfers', icon: Shuffle },
      { id: 'lots', label: 'Lots & Expiry', path: '/inventory/lots', icon: Layers3 },
      { id: 'serials', label: 'Serials', path: '/inventory/serials', icon: Barcode },
      { id: 'sku-mapping', label: 'SKU Mapping', path: '/inventory/sku-mapping', icon: GitCompare },
      { id: 'data-enrichment', label: 'Data Enrichment', path: '/inventory/data-enrichment', icon: DatabaseZap },
      { id: 'recipes', label: 'Recipes (BOM)', path: '/recipes', icon: FileText },
      { id: 'kits', label: 'Kits & Bundles', path: '/kits', icon: Boxes },
      { id: 'scan', label: 'Scan / Vision', path: '/scan', icon: ScanLine },
      { id: 'reference-images', label: 'Reference Images', path: '/reference-images', icon: Image },
    ],
  },
  {
    id: 'planning',
    label: 'Planning',
    items: [
      { id: 'mps', label: 'Production Planning', path: '/mps', icon: CalendarClock },
      { id: 'capacity', label: 'Capacity', path: '/capacity', icon: Gauge },
      { id: 'subcontracting', label: 'Subcontracting', path: '/subcontracting', icon: HeartHandshake },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'rma', label: 'Returns (RMA)', path: '/rma', icon: RotateCcw },
      { id: 'order-board', label: 'Order Board', path: '/orders/board', icon: PanelsTopLeft },
      { id: 'quotes', label: 'Quotes', path: '/quotes', icon: FileText },
      { id: 'packing', label: 'Packing', path: '/packing', icon: PackageOpen },
      { id: 'supply-chain', label: 'Supply Chain', path: '/supply-chain', icon: Truck },
      { id: 'approvals', label: 'Approvals', path: '/approvals', icon: CheckSquare },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'reports', label: 'Reports', path: '/reports', icon: BarChart3 },
      { id: 'anomalies', label: 'Anomalies', path: '/reports/anomalies', icon: BarChart3 },
      { id: 'integrations', label: 'Integrations', path: '/settings/integrations', icon: Link, allowedRoles: [UserRole.ADMIN, UserRole.MANAGER] },
      { id: 'quickbooks', label: 'QuickBooks', path: '/settings/quickbooks', icon: Link, allowedRoles: [UserRole.ADMIN, UserRole.MANAGER] },
      { id: 'jobs', label: 'Jobs', path: '/system/jobs', icon: ClipboardList, allowedRoles: [UserRole.ADMIN] },
      { id: 'stock-behavior-migration', label: 'Migration Review', path: '/system/stock-behavior-migration', icon: AlertTriangle, allowedRoles: [UserRole.ADMIN, UserRole.MANAGER] },
      { id: 'admin', label: 'Admin', path: '/system/admin', icon: UserCog, allowedRoles: [UserRole.ADMIN] },
      { id: 'diagnostics', label: 'Diagnostics', path: '/system/diagnostics', icon: Activity, allowedRoles: [UserRole.ADMIN] },
      { id: 'compliance', label: 'Compliance', path: '/system/compliance', icon: ShieldCheck, allowedRoles: [UserRole.ADMIN] },
      { id: 'webhooks', label: 'Webhooks', path: '/system/webhooks', icon: Webhook, allowedRoles: [UserRole.ADMIN] },
      { id: 'settings', label: 'Settings', path: '/settings', icon: Settings },
    ],
  },
];

const appVersion = __APP_VERSION__;
const versionLabel = /^[0-9a-f]{7,40}$/i.test(appVersion)
  ? appVersion.slice(0, 7)
  : `v${appVersion}`;

function userCanSeeNavItem(item: NavItem, role: UserRole | undefined): boolean {
  return !item.allowedRoles || (role !== undefined && item.allowedRoles.includes(role));
}

function getVisibleNavSections(role: UserRole | undefined): NavSection[] {
  return navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => userCanSeeNavItem(item, role)),
    }))
    .filter((section) => section.items.length > 0);
}

function sectionHasActiveRoute(section: NavSection, pathname: string): boolean {
  return section.items.some(
    (item) => item.path === '/' ? pathname === '/' : pathname.startsWith(item.path)
  );
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen } = useThemeStore();
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  // Track desktop (>= lg) so the off-canvas drawer always renders the full nav,
  // independent of the rail-collapse state (which only applies at lg+).
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
      if (e.matches) setMobileNavOpen(false); // drawer is mobile-only
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setMobileNavOpen]);

  // Effective rail-collapse: only on desktop. The mobile drawer is always full.
  const collapsed = isDesktop && sidebarCollapsed;
  const visibleNavSections = getVisibleNavSections(role);
  const allNavItems = visibleNavSections.flatMap((s) => s.items);

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const renderNavLink = (item: NavItem, collapsed: boolean) => {
    const Icon = item.icon;

    if (collapsed) {
      return (
        <Tooltip key={item.id} delayDuration={0}>
          <TooltipTrigger asChild>
            <NavLink
              to={item.path}
              aria-label={item.label}
              className={({ isActive }) =>
                cn(
                  'flex h-10 w-10 items-center justify-center mx-auto transition-all relative',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )
              }
              style={({ isActive }) => isActive ? {
                background: 'hsl(var(--primary) / 0.08)',
              } : {}}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <div
                      className="absolute left-0 top-1 bottom-1 w-[3px]"
                      style={{ background: 'hsl(var(--primary))', borderRadius: '0 2px 2px 0' }}
                    />
                  )}
                  <Icon className="h-[18px] w-[18px]" />
                </>
              )}
            </NavLink>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={12}
            className="font-mono text-xs uppercase tracking-wide"
          >
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <NavLink
        key={item.id}
        to={item.path}
        aria-label={item.label}
        className={({ isActive }) =>
          cn(
            'flex h-9 items-center gap-3 px-3 transition-all relative',
            isActive
              ? 'text-primary font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )
        }
        style={({ isActive }) => isActive ? {
          background: 'hsl(var(--primary) / 0.08)',
        } : {}}
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <div
                className="absolute left-0 top-1 bottom-1 w-[3px]"
                style={{ background: 'hsl(var(--primary))', borderRadius: '0 2px 2px 0' }}
              />
            )}
            <Icon className="h-[16px] w-[16px] shrink-0" />
            <span className="truncate text-sm">{item.label}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      id="sidebar-nav"
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-border bg-card transition-all duration-300',
        // Width: full nav as a drawer below lg; rail-collapsible at lg+.
        'w-64',
        collapsed ? 'lg:w-16' : 'lg:w-64',
        // Slide off-canvas below lg unless the drawer is open; always shown at lg+.
        mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0 print:hidden'
      )}
      style={{
        boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.03), 2px 0 8px rgba(0,0,0,0.2)',
      }}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-border px-4">
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <img
              src="/favicon.png"
              alt=""
              className="h-9 w-9 object-cover shadow-[0_0_12px_rgba(20,184,166,0.28)]"
            />
            <div className="flex flex-col">
              <span className="text-base font-bold tracking-forge-display text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
                StockForge
              </span>
              <span className="text-forge-label text-muted-foreground" style={{ fontSize: '9px', letterSpacing: '0.12em' }}>
                MFG INTELLIGENCE
              </span>
            </div>
          </div>
        ) : (
          <img
            src="/favicon.png"
            alt=""
            className="mx-auto h-9 w-9 object-cover shadow-[0_0_12px_rgba(20,184,166,0.28)]"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {collapsed ? (
          // Collapsed: flat icon list
          allNavItems.map((item) => renderNavLink(item, true))
        ) : (
          // Expanded: grouped sections
          visibleNavSections.map((section) => {
            const isActive = sectionHasActiveRoute(section, location.pathname);
            const isCollapsed = collapsedSections.has(section.id);
            // Single-item sections (Dashboard) don't get a header
            if (section.items.length === 1) {
              return (
                <div key={section.id}>
                  {renderNavLink(section.items[0], false)}
                </div>
              );
            }

            return (
              <div key={section.id} className="pt-1.5">
                <button
                  onClick={() => toggleSection(section.id)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-1 text-[10px] font-mono uppercase tracking-widest transition-colors',
                    isActive ? 'text-muted-foreground' : 'text-muted-foreground/60',
                    'hover:text-muted-foreground'
                  )}
                >
                  {section.label}
                  <ChevronDown
                    className={cn(
                      'h-3 w-3 transition-transform',
                      isCollapsed && '-rotate-90'
                    )}
                  />
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5 mt-0.5">
                    {section.items.map((item) => renderNavLink(item, false))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </nav>

      {/* Bottom section: System status + collapse */}
      <div className="border-t border-border">
        {/* System status indicator */}
        {!collapsed && (
          <div className="px-4 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-success-500" style={{ boxShadow: '0 0 6px rgba(34, 197, 94, 0.5)' }} />
                <span className="text-forge-label text-muted-foreground" style={{ fontSize: '10px' }}>
                  SYSTEM ONLINE
                </span>
              </div>
              <span className="font-mono text-[10px] text-muted-foreground/70">
                {versionLabel}
              </span>
            </div>
          </div>
        )}

        {/* Collapse toggle (rail control, desktop only — mobile uses the drawer) */}
        <div className="hidden p-2 lg:block">
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-9 w-full', collapsed && 'w-11')}
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 mr-2" />
                <span className="text-xs font-mono uppercase tracking-wide">Collapse</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
}
